import React, {useEffect, useMemo, useState} from "react";

import {useParams} from "react-router-dom";
import {ScopedExtroProvider} from "components/extro/ScopedExtroProvider";
import {
    GetPrsesToPayQueryResult,
    PrsPaymentSettings,
    PrsToPay,
    useGetPrsPaymentSettingsQuery,
    useGetPrsToPayQuery
} from "store/post-results-services/postResultsServicesApi";
import {PageContainer} from 'components';
import { getTaxInfo } from 'pages/helper/taxInfo';

import {Message} from "@britishcouncil/react-extro-ui";
import {LoadingCard} from "@britishcouncil/react-solas-ors3/dist/components/loaders/LoadingCard";

import CommonLayout from "../components/CommonLayout/CommonLayout";
import {SelectPaymentMainSection, SelectPaymentSideSection} from "../components/PaymentMethod";

import * as S from "./SelectPrsPaymentMethod.style";

export type PrsPaymentNavigationSource = 'external' | 'list' | 'newPrs';

export type SelectPrsPaymentMethodParams = {
    prsIds: string[],
    navigationSource: PrsPaymentNavigationSource
}

export const SelectPrsPaymentMethod = () => {
    const {paramsBase64String} = useParams();

    const params = useMemo<SelectPrsPaymentMethodParams>(() => {
        if (!paramsBase64String)
            return {} as SelectPrsPaymentMethodParams;
        const decodedJson = atob(paramsBase64String);
        return JSON.parse(decodedJson) as SelectPrsPaymentMethodParams;
    }, [paramsBase64String]);

    const getPrsPaymentSettingsQueryData = useGetPrsPaymentSettingsQuery({prsIds: params.prsIds});
    const getPrsToPayQueryData = useGetPrsToPayQuery({prsIds: params.prsIds});

    if (getPrsToPayQueryData.isLoading || getPrsPaymentSettingsQueryData.isLoading)
        return <Root><LoadingCard/></Root>

    if (getPrsToPayQueryData.isError || getPrsPaymentSettingsQueryData.isError)
        return <Root><Error/></Root>

    const {data: {allowOfflinePayment, allowOnlinePayment} = {allowOfflinePayment: false, allowOnlinePayment: false}} = getPrsPaymentSettingsQueryData;

    if (!allowOnlinePayment && !allowOfflinePayment)
        return <Root><NotPaymentMethod/></Root>

    return <Root>
        <SelectPrsPaymentMethodContent
            prsPaymentSettings={getPrsPaymentSettingsQueryData.data!}
            prsToPay={getPrsToPayQueryData.data!}
            navigationSource={params.navigationSource}
        />
    </Root>

};

const Root = ({children}: { children?: React.ReactNode }) =>
    <ScopedExtroProvider>
        <S.StyledPageContainer>
            {children}
        </S.StyledPageContainer>
    </ScopedExtroProvider>;

const Error = () => <PageContainer>
    <Message
        header={`Error`}
        variant="error"
        sideContentAlign='container'
    >
        There was an error processing your payment. Please try payment again
    </Message>
</PageContainer>

const NotPaymentMethod = () => <PageContainer>
    <Message
        header={`Error`}
        variant="error"
        sideContentAlign='container'
    >
        Payment not found. Please try again later.
    </Message>
</PageContainer>

const SelectPrsPaymentMethodContent = ({prsToPay, prsPaymentSettings, navigationSource }: { prsToPay: GetPrsesToPayQueryResult, prsPaymentSettings: PrsPaymentSettings, navigationSource: PrsPaymentNavigationSource }) => {
    const [justCreatedPrs, setJustCreatedPrs] = useState<PrsToPay | null>(null);

    const taxInfo = getTaxInfo(prsToPay.prss[0].taxRate, Number(prsToPay.total));

    useEffect(() => {
        if (navigationSource === "newPrs" && prsToPay && prsToPay.prss.length === 1) {
            setJustCreatedPrs(prsToPay.prss[0]);
        }
    }, []);

    return (
        <CommonLayout
            alignCenter={true}
            topSection={
                <>
                    <S.Header>
                        <span>Payment method</span>
                    </S.Header>

                    {justCreatedPrs &&
                        <div style={{marginBottom: '20px'}}>
                            <Message
                                header={`Request number ${justCreatedPrs.referenceId} has been created.`}
                                sideContent={`${justCreatedPrs.total} ${justCreatedPrs.currency}`}
                                variant="success"
                                sideContentAlign='container'
                            >
                                We have received your application, please be patient. Our support team will look into the matter at the earliest available time.
                            </Message>
                        </div>
                    }
                </>

            }
            mainSection={<SelectPaymentMainSection
                prses={prsToPay}
                prsPaymentSettings={prsPaymentSettings}
                showBackButton={navigationSource === 'list'}/>}
            sideSection={<SelectPaymentSideSection
                prss={prsToPay.prss}
                total={`${prsToPay.total} ${prsToPay.currency}`}
                taxInfo={taxInfo}/>}
        />

    )

}
